import moment from 'moment';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import {
  AllocationMedium,
  PlanningLevelConfig,
  ResourceType,
} from 'api/resource-management/types';
import { AllocationModalParams } from 'components/organisms/resource-management/manage-allocation';
import { EventAddParams } from 'components/organisms/resource-management/scheduler-config/listeners';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { shiftDateIfOnNonWorkingDay } from 'components/organisms/resource-management/utils/dates';
import {
  idForNewAllocation,
  newAllocationEvent,
} from 'components/organisms/resource-management/utils/events-conversion/common';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { CommonManagerProps } from '../../types';

interface Props extends CommonManagerProps {
  setCurrentEvent: Dispatch<SetStateAction<AllocationEventModel | undefined>>;
  setAllocationModalParams: Dispatch<
    SetStateAction<AllocationModalParams | undefined>
  >;
}
type ReturnProps = (params: EventAddParams) => void;
const DEFAULT_ALLOCATION_PER_DAY = 60; // in minutes

export const useAllocationAddHandler = (props: Props): ReturnProps => {
  const {
    schedulerRef,
    groupBy,
    workingDaysConfig,
    excludeNonWorkingDays,
    setCurrentEvent,
    setAllocationModalParams,
    resourceManagementConfig,
  } = props;

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  const fallbackCapacity =
    DEFAULT_ALLOCATION_PER_DAY * (isHoursPerWeek ? 40 : 8);
  const users = useGetAllActiveUsersMap();

  return useCallback(
    ({ startDate, endDate, resource }: EventAddParams) => {
      const startDateMoment = shiftDateIfOnNonWorkingDay(
        moment(startDate).startOf('day'),
        workingDaysConfig,
      );
      const endDateMoment = moment(endDate).endOf('day');
      const userId = resource.get('userId');
      const teamId = resource.get('teamId');
      const roleId = resource.get('roleId');
      const referenceTeamId = resource.get('referenceTeam')?.teamId;
      const projectId = resource.get('projectId');
      const numberOfWorkingDays = numberOfWorkingDaysBetweenDates(
        startDateMoment,
        endDateMoment,
        workingDaysConfig,
      );

      const userCapacity = userId != null ? users[userId]?.capacity * 60 : null; // in hours per week
      const capacity = userCapacity
        ? userCapacity / (isHoursPerWeek ? 1 : 5)
        : fallbackCapacity;

      setAllocationModalParams({
        ...(userId != null
          ? {
              type: ResourceType.User,
              userId,
              ...(referenceTeamId
                ? {
                    roleId,
                    referenceTeamId,
                  }
                : {}),
            }
          : {
              teamId,
              roleId,
              type: ResourceType.Team,
            }),
        billable: true,
        minutes: capacity,
        projectId,
        workingDays: numberOfWorkingDays,
        medium: AllocationMedium.Hard,
        startDate,
        endDate,
      });
      const tempAllocation = newAllocationEvent(
        groupBy,
        {
          ...(userId != null
            ? {
                type: ResourceType.User,
                userId,
                ...(referenceTeamId
                  ? {
                      roleId,
                      referenceTeamId,
                    }
                  : {}),
                overAllocations: [],
              }
            : {
                teamId,
                roleId,
                type: ResourceType.Team,
              }),
          minutes: capacity,
          billable: true,
          startDate: startDateMoment.format(DEFAULT_DATE_FORMAT),
          endDate: endDateMoment.format(DEFAULT_DATE_FORMAT),
          totalMinutes: capacity * (isHoursPerWeek ? 1 : numberOfWorkingDays),
          projectId,
          id: idForNewAllocation(),
          medium: AllocationMedium.Hard,
        },
        {
          excludeNonWorkingDays,
          workingDaysConfig,
        },
        resourceManagementConfig?.softAllocations.enabled,
      );
      const [allocationRef] = schedulerRef.current.instance.eventStore.add({
        ...tempAllocation,
        active: true,
      });

      setCurrentEvent(allocationRef);
    },
    [
      workingDaysConfig,
      setAllocationModalParams,
      groupBy,
      excludeNonWorkingDays,
      resourceManagementConfig?.softAllocations.enabled,
      schedulerRef,
      setCurrentEvent,
      isHoursPerWeek,
      users,
      fallbackCapacity,
    ],
  );
};
